import { voidFunction } from '@avantia/client-and-server-utilities';
import { destructureControlId } from '@avantia/questionset-model';
import { hpClaimsAlertEventType, registerClaimStartedEvent } from '../actions/eventActions';
import { HpClaimsDebugFlags, logInfo } from '../clientLogging';
import { HpModalButtonTypes, HpTriggerFieldEventProps } from '../engineClient/actions/eventActionTypes';
import { bindCustomChangeEvents } from '../engineClient/actions/eventActions';
import {
  createFieldChangeData as createFieldChangeServerData,
  logEventWithMask
} from '../engineClient/tools/eventLogger';
import { getEventDetail } from '../engineClient/tools/utilities';

window.dataLayer = window.dataLayer || [];
const keySeparator = '~';
const modalSuffix = 'Modal';
export function setupGtmEventHandler(): void {
  bindCustomChangeEvents({
    onFieldChange: ({ data }) => {
      const { qinteractiontype, qdatalocator, qvalue } = createFieldChangeData(data);
      const { question } = data;
      const { gtmTag } = question || {};
      if (gtmTag && gtmTag.includes(modalSuffix)) {
        return;
      }

      const eventData: HpGtmData = {
        event: 'qinteraction',
        qinteractiontype,
        qdatalocator,
        qvalue
      };

      logEventWithMask({
        eventData,
        getEvent: data.getEvent,
        logEventFunction: addData,
        propertyToMask: 'qvalue'
      });
    },
    onToggleElement: ({ data }) => {
      const { question, getEvent, targetType } = data;
      const event = getEvent();
      const { target } = getEventDetail(event);
      addData({
        event: 'qinteraction',
        qinteractiontype: `${targetType}click`,
        qdatalocator: question.gtmTag || (question.mappingField as string) || (target ? target.id : '')
      });
    },
    onNavigationChange: ({ data }) => {
      const { activeSection } = data;
      const { gtmTag, title } = activeSection;
      addData({
        event: 'virtualpageview',
        virtualpage: gtmTag || title,
        pagetitle: title
      });
    },
    onNavigationBarAction: ({ data }) => {
      const { getEvent, elementType } = data;
      const event = getEvent();
      const { target } = getEventDetail(event);
      let sectionName = target.title || '';
      sectionName = sectionName
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');

      let gtmTag = `Breadcrumb_${sectionName}`;
      if (elementType === 'menu') {
        gtmTag += `_top_Link`;
      } else if (elementType === 'submenu') {
        gtmTag += `_Top_Dropdown`;
      }

      addData({
        event: 'qinteraction',
        qinteractiontype: 'pagenavigationclick',
        qdatalocator: gtmTag
      });
    },
    onPageLoad: ({ data }) => {
      const { gtmTag, title } = data;
      if (gtmTag) {
        addData({
          event: 'virtualpageview',
          virtualpage: gtmTag,
          pagetitle: title
        });
      }
    },

    onGeneralAction: ({ data }) => {
      const { gtmTag, getEvent } = data;
      const event = getEvent();
      const { target, eventType } = getEventDetail(event);
      let textContent: string | null = null;
      let qinteractiontype: string | null = null;
      if (eventType === hpClaimsAlertEventType) {
        ({ textContent } = target || {});
        qinteractiontype = 'alertdisplayed';
      }

      addData({
        event: 'qinteraction',
        qinteractiontype: qinteractiontype || `primaryactionbuttonclick`,
        qdatalocator: gtmTag || (target ? target.id : ''),
        qvalue: textContent || ''
      });
    },
    onHelpInfoAction: voidFunction,
    onModalAction: ({ data }) => {
      // This will only be called when the modal is triggered on button click (DeleteFloodLink_0) and when we close the modal (Flood_DeleteDialogCancelBtn)
      const { question, getEvent, buttonType } = data;
      const event = getEvent();
      const { gtmTag } = question;
      const { target } = getEventDetail(event);
      const { id } = target;
      const { groupNumber } = destructureControlId(id, { requirePrefix: false });
      let qdatalocator, qinteractiontype, qvalue;

      if (gtmTag) {
        if (gtmTag.includes(keySeparator)) {
          ({ qdatalocator, qinteractiontype, qvalue } = createGtmTagByButtonType(gtmTag, buttonType, target));
        } else {
          const primaryactionbutton = 'primaryactionbuttonclick';
          if (groupNumber && buttonType && buttonType === 'show') {
            // i.e DeleteFloodLink_0
            qdatalocator = gtmTag.replace('{groupNumber}', String(groupNumber - 1));
            qinteractiontype = gtmTag.startsWith('Delete') ? 'secondaryactionbuttonclick' : primaryactionbutton;
          } else if (buttonType === 'cancel') {
            // i.e Flood_DeleteDialogCancelBtn
            ({ qdatalocator, qinteractiontype } = createModalActionData(gtmTag, buttonType));
          } else if (buttonType === 'show') {
            qdatalocator = gtmTag;
            qinteractiontype = primaryactionbutton;
          } else if (buttonType === 'confirm') {
            qdatalocator = `${gtmTag}_Confirm`;
            qinteractiontype = primaryactionbutton;
          }
        }

        addData({
          event: 'qinteraction',
          qinteractiontype,
          qdatalocator,
          qvalue
        });
      }
    },
    onLinkFollowed: ({ data }) => {
      const { link } = data;
      let key: string;
      let tagName: undefined | string;
      let interactionType: undefined | string;
      if (link.tagName === 'BUTTON') {
        let text: string;
        ({ id: key, innerText: text } = link as HTMLButtonElement);
        key = key || text;
      } else {
        ({ href: key } = link as HTMLAnchorElement);
        const { name, type } = link;

        if (name) {
          tagName = name.replace(/\s+/g, '');
        }

        if (type) {
          interactionType = type;
        }
      }

      addData({
        event: 'qinteraction',
        qdatalocator: key,
        qvalue: tagName,
        qinteractiontype: interactionType || 'moreinfoclick'
      });
    }
  });

  registerClaimStartedEvent(({ data }) => {
    const { agentName } = data;
    addData({
      event: 'qsmetadata',
      agentName
    });
  });

  function addData(data: HpGtmData): void {
    window.dataLayer.push(data);
    logInfo(data as any, HpClaimsDebugFlags.Gtm);
  }
}

interface HpGtmData {
  event?: string;
  qdatalocator?: string;
  qinteractiontype?: string;
  qvalue?: string;
  virtualpage?: string;
  pagetitle?: string;
  agentName?: string;
  version?: string;
}

declare global {
  interface Window {
    dataLayer: HpGtmData[];
  }
}

interface CreateGtmFieldChangeDataResult {
  qdatalocator: string;
  qinteractiontype: string;
  qvalue: string;
}

function createFieldChangeData(data: HpTriggerFieldEventProps): CreateGtmFieldChangeDataResult {
  const { getEvent, question, section, key } = data;
  const event = getEvent();
  const { dataType, gtmTag, mappingField } = question || {};
  const eventData = createFieldChangeServerData({
    data,
    answersId: 'x',
    sessionId: 'y',
    timeStamp: event.timeStamp
  });
  let { value } = eventData;
  let qinteractiontype = event.type === 'click' ? 'click' : 'valueintroduced';
  let qdatalocator = gtmTag || (mappingField as string) || '';
  if (section && key === 'nav-bar') {
    qinteractiontype = 'pagenavigationclick';
    qdatalocator = section.gtmTag || section.title;
    value = section.title;
  } else if (dataType === 'button' && gtmTag) {
    if (!value) {
      const { buttonText } = eventData;
      if (buttonText) {
        value = buttonText;
      }
    }

    if (gtmTag.endsWith('_BackBtn') || gtmTag.endsWith('_NextBtn')) {
      qinteractiontype = 'pagenavigationclick';
    } else {
      // In Q&B this could either be 'primaryactionbuttonclick' or 'secondaryactionbuttonclick'
      // At present in this journey we have no way of knowing, so we're going with 'primary'.

      if (gtmTag.startsWith('Delete')) {
        // This is the confirm button of the dialog, needs to be converted to i.e Flood_DeleteDialogCancelBtn.
        ({ qdatalocator, qinteractiontype } = createModalActionData(gtmTag, 'confirm'));
      } else {
        qinteractiontype = 'primaryactionbuttonclick';
      }
    }
  }

  return { qinteractiontype, qdatalocator, qvalue: value || '' };
}

interface CreateGtmModalActionDataResult {
  qdatalocator: string;
  qinteractiontype: string;
}

function createModalActionData(gtmTag: string, buttonType: HpModalButtonTypes): CreateGtmModalActionDataResult {
  const group = gtmTag.match('Delete(.*)Link_');
  const groupName = group && group[1];
  const action = buttonType === 'confirm' ? 'Confirm' : 'Cancel';
  const qdatalocator = `${groupName}_DeleteDialog${action}Btn`;
  const qinteractiontype = gtmTag.startsWith('Delete') ? 'secondaryactionbuttonclick' : 'primaryactionbuttonclick';
  return { qinteractiontype, qdatalocator };
}

function createGtmTagByButtonType(gtmTag: string, buttonType: HpModalButtonTypes, target?: HTMLInputElement) {
  let qdatalocator = '';
  let qinteractiontype = '';
  let qvalue = '';

  const modalTags = gtmTag.split(keySeparator);
  if (modalTags.length === 5) {
    const modalName = `${modalTags[0]}`;
    if (buttonType === 'show') {
      qdatalocator = `${modalName.replace(modalSuffix, '')}_${modalTags[1]}`;
      qinteractiontype = 'pagenavigationclick';
      qvalue = modalTags[4];
    } else if (buttonType === 'confirm') {
      qdatalocator = `${modalName}_${modalTags[2]}`;
      qinteractiontype = 'pagenavigationclick';
    } else if (buttonType === 'cancel') {
      if (target && target.textContent && target.textContent === '×Close') {
        qdatalocator = `${modalName}_CancelBtn`;
        qinteractiontype = 'pagenavigationclick';
      } else {
        qdatalocator = `${modalName}_${modalTags[3]}`;
        qinteractiontype = 'pagenavigationclick';
      }
    }
  }

  return { qinteractiontype, qdatalocator, qvalue };
}
